import { DocumentRenderData, RequiredField } from '../types'
import {
  MergeField,
  SignatureBlock,
  IntermediaryBlock,
  QISignatureBlock,
  blank,
  formatDate,
  formatUSD,
} from './shared'

export const disbursementAuthorizationRequiredFields: RequiredField[] = [
  { key: 'exchanger.names', description: 'Exchanger full name', sourceHub: 'disbursement', sourceStep: 'Disbursement Form' },
  { key: 'disbursement.amount', description: 'Disbursement amount', sourceHub: 'disbursement', sourceStep: 'Disbursement Form' },
  { key: 'disbursement.beneficiaryName', description: 'Funds will be disbursed to', sourceHub: 'disbursement', sourceStep: 'Disbursement Form' },
  { key: 'disbursement.recipientBankName', description: 'Recipient bank name', sourceHub: 'disbursement', sourceStep: 'Disbursement Form' },
  { key: 'disbursement.escrowDate', description: 'Disbursement date', sourceHub: 'disbursement', sourceStep: 'Disbursement Form' },
]

interface Props {
  data: DocumentRenderData
}

function methodLabel(method: string | undefined): string {
  switch (method) {
    case 'wire':
      return 'Wire Transfer'
    case 'check':
      return 'Check'
    case 'ach':
      return 'ACH'
    default:
      return ''
  }
}

export function DisbursementAuthorizationTemplate({ data }: Props) {
  const { exchanger, disbursement, signature, bundleMeta } = data
  const exchangerLegal = exchanger.legalEntityDescription || exchanger.names
  const amountFormatted = formatUSD(disbursement?.amount)
  const dateFormatted = formatDate(disbursement?.escrowDate)
  const bankName = disbursement?.bankName ?? disbursement?.recipientBankName ?? 'First Foundation Bank'
  const accountNumber = disbursement?.exchangeAccountNumber

  return (
    <div className="space-y-6 text-sm text-foreground/85 font-serif">
      <div className="text-center">
        <p className="text-[11px] uppercase tracking-widest text-muted-foreground">EXHIBIT &ldquo;A&rdquo;</p>
        <h4 className="text-xl font-bold tracking-wide text-foreground mt-1">
          DISBURSEMENT REQUEST
        </h4>
        <p className="text-xs text-muted-foreground/80 mt-1 italic">
          §1031 Qualified Escrow — Funds Release
        </p>
      </div>

      <div className="rounded-md border border-border bg-card/50 p-3 space-y-1 text-xs">
        <MergeField label="Open 1031, Inc. File:" value={bundleMeta?.open1031FileNumber} width={28} />
        <MergeField label="Exchanger:" value={exchangerLegal} width={40} />
        <MergeField label="Exchange Account No.:" value={accountNumber} width={28} />
      </div>

      <section className="space-y-2">
        <p className="text-xs leading-relaxed">
          <span className="font-mono">{blank(bankName, 30)}</span>{' '}
          (&ldquo;<strong>Bank</strong>&rdquo;) is hereby directed to pay from the Exchange Proceeds
          established and maintained under that certain Exchange Agreement by and among Open 1031,
          Inc. (&ldquo;Intermediary&rdquo;),{' '}
          <span className="font-mono">{blank(exchangerLegal, 35)}</span>{' '}
          (&ldquo;Exchanger&rdquo;) (Account{' '}
          <span className="font-mono">{blank(accountNumber, 18)}</span>) (&ldquo;Account&rdquo;), and
          Bank (the &ldquo;Deposit Agreement&rdquo;) the Disbursement Amount set forth below, plus
          any and all accrued interest, if applicable.
        </p>
      </section>

      <section className="rounded-md border border-border bg-card/50 p-3 space-y-2 text-xs">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
          <p>
            <span className="font-semibold">Disbursement Amount:</span>{' '}
            <span className="font-mono">{blank(amountFormatted, 20)}</span>
          </p>
          <p>
            <span className="font-semibold">Disbursement Date:</span>{' '}
            <span className="font-mono">{blank(dateFormatted, 20)}</span>
          </p>
        </div>
        <p>
          <span className="font-semibold">Funds will be Disbursed to:</span>{' '}
          <span className="font-mono">{blank(disbursement?.beneficiaryName, 45)}</span>
        </p>
        <p>
          <span className="font-semibold">Method of Disbursement:</span>{' '}
          <span className="font-mono">{blank(methodLabel(disbursement?.disbursementMethod), 25)}</span>
        </p>
        {disbursement?.disbursementMethod === 'wire' && (
          <p className="text-[11px] italic text-muted-foreground">
            (Wire transfer — wiring instructions{' '}
            {disbursement.wireInstructionsAttached ? 'attached' : 'must be attached'}.)
          </p>
        )}
      </section>

      <p className="text-xs leading-relaxed">
        Upon execution of this Disbursement Request, the undersigned Exchanger hereby certifies that
        he/she has reviewed supporting documentation and this payment is in accordance with the
        requirements set forth in the Agreement by and between the Parties.
      </p>

      <section className="border-t-2 border-border pt-4 space-y-4">
        <SignatureBlock
          label="EXCHANGER:"
          signerName={exchangerLegal}
          signature={signature}
          borderColorClass="border-orange-500"
        />
        <div className="pt-2">
          {signature?.captured ? (
            <QISignatureBlock signature={signature} />
          ) : (
            <IntermediaryBlock data={data} />
          )}
        </div>
      </section>
    </div>
  )
}
